import { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { PageIntro, PageBadgeBand, Section } from "@/components/site/PageShell";
import { SITE } from "@/constants/testIds";
import { useMeta } from "@/hooks/useMeta";
import { useJsonLd } from "@/hooks/useJsonLd";
import { faqPageSchema, breadcrumbSchema } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

/* , FAQ CONTENT, every answer maps to the claims sheet , */
const FAQ_GROUPS = [
  {
    group: "How it works",
    items: [
      {
        q: "What does the app actually do?",
        a: "You start recording manually; the app captures audio, timestamp, and GPS until you end the session. Each recording is encrypted (AES-256) and hash-sealed (SHA-256) at the moment of capture, then batch-uploaded to secure cloud storage so the record survives your phone.",
      },
      {
        q: "Can I play back or view my own recordings inside the app?",
        a: "No. Sealed recordings can't be played back inside the app \u2014 not by you, not by us, not by anyone. This is deliberate: it makes the record impossible to pressure someone into playing, editing, or leaking. To retrieve a recording (for a case, a claim, or your defense), submit a legal order or a signed release along with your encryption/decryption key. YeboNadi decrypts the record and delivers it via secure cloud storage. A retrieval fee applies.",
      },
      {
        q: "How do I get a recording to a lawyer, employer, or officer?",
        a: "Recordings are shared via a secure platform and you can share it from there.",
      },
      {
        q: "What if I lose my encryption/decryption key?",
        a: "Recordings are lost without your encryption/decryption key. Share the key with trusted persons.",
      },
      {
        q: "What happens to old recordings when my storage fills up?",
        a: "Older recordings are overwritten on a rolling basis to make room for new ones. Mark anything as \u201cKeep\u201d and it is protected from that rolling overwrite for as long as your plan is active. Contact YeboNadi if you want a larger storage space.",
      },
      {
        q: "Will it drain my battery?",
        a: "Battery usage depends on session length, phone model, and OS power settings. We publish device walkthroughs on our YouTube channel, the current one on your device is the honest place to see how the app behaves in practice.",
      },
    ],
  },
  {
    group: "If things go wrong",
    items: [
      {
        q: "What if I lose my phone \u2014 or someone takes it?",
        a: "Your sealed recordings are already batch-uploaded to encrypted cloud storage. Sign in on a new device and your vault comes back. Losing the phone does not lose the record.",
      },
      {
        q: "Can someone force me to delete my evidence?",
        a: "No. Your data on the platform cannot be deleted if you use the \u201cKeep\u201d feature.",
      },
      {
        q: "What does the retrieval fee cover?",
        a: "Cost to process/host your downloaded data. Refer to the app for current retrieval pricing.",
      },
      {
        q: "Can YeboNadi listen to my recordings?",
        a: "No. Your recordings are encrypted on your phone before they leave the device. We do not have a \u201clisten in\u201d button. See the Security & Trust page for a longer explanation.",
      },
    ],
  },
  {
    group: "Legal & consent",
    items: [
      {
        q: "Is this legal?",
        a: "It depends where you are. Federal law and most US states allow one-party consent \u2014 you can record a conversation you are part of. 13 US states require all-party consent, which means every person in the conversation must know and agree. Outside the US, check the recording-consent law where you are. The Security & Trust page has the state list and a short explainer.",
      },
      {
        q: "Am I allowed to record?",
        a: "You are responsible for complying with the recording-consent laws that apply where you record. When in doubt, get advice before you press record.",
      },
      {
        q: "Is a YeboNadi recording admissible in court?",
        a: "Whether any recording is admissible depends on the law of your jurisdiction and the facts of your case. YeboNadi seals what you captured; the court decides what it will accept. Talk to a lawyer for anything you plan to use as evidence.",
      },
    ],
  },
  {
    group: "Security & privacy",
    items: [
      {
        q: "What can YeboNadi see, and what can\u2019t we see?",
        a: "We can see your account email, your storage usage, the timestamps of your recordings and the location of the recording. We can see the sealed file itself, but we are unable to listen to or transcribe the words that were said or to anyone else\u2019s device. Content is encrypted on your phone before it reaches us.",
      },
      {
        q: "What is the Undertaking?",
        a: "A binding pledge every user signs before their first recording \u2014 not to use YeboNadi for blackmail, harassment, coercion, or unlawful surveillance. Four short clauses. Read them on the Undertaking page.",
      },
],
  },
  {
    group: "Account & billing",
    items: [
      {
        q: "How much does it cost?",
        a: "Free to start. Plus is $5.99 / month for 5 GB of sealed cloud storage. Pro is $9.99 / month for 10 GB. Full details on the Pricing page.",
      },
      {
        q: "Can I cancel any time?",
        a: "Yes. Cancel from inside the app. You keep access for the rest of the period you already paid for; nothing else charges after that.",
      },
      {
        q: "What happens if I miss a payment?",
        a: "Your sealed recordings remain encrypted in cloud storage during a grace period. Bring the account current within the grace window and everything reactivates. If not, older recordings age out on the retention schedule described on the Privacy page.",
      },
    ],
  },
];

/* Flatten so search filters across groups without losing group labels */
function normalize(str) {
  return (str || "").toLowerCase();
}

export default function Faq() {
  useMeta({
    title: "FAQ, YeboNadi",
    description:
      "Straight answers about YeboNadi: how the seal works, what we can and can't see, consent laws, pricing, the Undertaking, and what happens if your phone is lost or taken. 18 plain-English answers.",
    path: "/faq",
  });

  // FAQPage + BreadcrumbList JSON-LD for rich snippets
  const allItems = useMemo(
    () => FAQ_GROUPS.flatMap((g) => g.items),
    []
  );
  useJsonLd("faq-page", faqPageSchema(allItems));
  useJsonLd(
    "faq-crumbs",
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "FAQ", path: "/faq" },
    ])
  );

  const [query, setQuery] = useState("");
  const q = normalize(query.trim());

  const filteredGroups = useMemo(() => {
    if (!q) return FAQ_GROUPS;
    return FAQ_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          normalize(it.q).includes(q) ||
          normalize(it.a).includes(q) ||
          normalize(g.group).includes(q),
      ),
    })).filter((g) => g.items.length > 0);
  }, [q]);

  const totalMatches = filteredGroups.reduce(
    (n, g) => n + g.items.length,
    0,
  );

  // E7, fire `faq_search_no_results` when a real query yields zero results.
  // Debounced 800 ms; deduped so the same query doesn't fire twice.
  const lastLoggedRef = useRef("");
  useEffect(() => {
    if (q.length < 3) return;
    if (totalMatches > 0) return;
    if (lastLoggedRef.current === q) return;
    const t = setTimeout(() => {
      lastLoggedRef.current = q;
      trackEvent("faq_search_no_results", { query: q });
    }, 800);
    return () => clearTimeout(t);
  }, [q, totalMatches]);

  return (
    <div data-testid={SITE.faqPage}>
      <PageIntro
        kicker="FAQ"
        title="Straight answers."
        lede="Short, plain-English answers to what people actually ask. If yours isn't here, email info@concepts-simplified.com and we'll add it."
      />

      {/* Search */}
      <Section className="!pt-0">
        <div className="max-w-3xl">
          <label htmlFor="faq-search" className="sr-only">
            Search FAQ
          </label>
          <div className="relative">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--yn-muted)] pointer-events-none"
              aria-hidden="true"
            />
            <Input
              id="faq-search"
              type="search"
              inputMode="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search, e.g. battery, cancel, consent, court"
              data-testid={SITE.faqSearchInput}
              className="h-12 pl-11 pr-11 text-base border-[color:var(--yn-border)] focus-visible:ring-[color:var(--yn-primary)]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[color:var(--yn-muted)] hover:text-[color:var(--yn-ink)] hover:bg-[color:var(--yn-subtle)]"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <p className="mt-3 text-xs text-[color:var(--yn-muted)]">
            {q
              ? `${totalMatches} ${totalMatches === 1 ? "match" : "matches"}`
              : `${FAQ_GROUPS.reduce((n, g) => n + g.items.length, 0)} questions across 5 topics`}
          </p>
        </div>
      </Section>

      {/* Accordion */}
      <Section className="!pt-0" data-testid={SITE.faqAccordion}>
        <div className="max-w-3xl">
          {filteredGroups.length === 0 ? (
            <div
              className="rounded-2xl border border-[color:var(--yn-border)] bg-[color:var(--yn-subtle)] p-8 text-center"
              data-testid={SITE.faqEmpty}
            >
              <p className="font-heading text-lg font-semibold text-[color:var(--yn-ink)]">
                No answers matched &ldquo;{query}&rdquo;.
              </p>
              <p className="mt-2 text-[15px] text-[color:var(--yn-muted)]">
                Try a shorter word, or email{" "}
                <a
                  href="mailto:info@concepts-simplified.com"
                  className="text-[color:var(--yn-primary)] hover:underline"
                >
                  info@concepts-simplified.com
                </a>{" "}
                and a human will answer.
              </p>
            </div>
          ) : (
            filteredGroups.map((group) => (
              <div key={group.group} className="mb-12 last:mb-0">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)] mb-4">
                  {group.group}
                </p>
                <Accordion
                  type="single"
                  collapsible
                  className="w-full"
                >
                  {group.items.map((item, i) => (
                    <AccordionItem
                      key={item.q}
                      value={`${group.group}-${i}`}
                      className="border-[color:var(--yn-border)]"
                      data-testid={SITE.faqItem}
                    >
                      <AccordionTrigger
                        className="font-heading text-lg font-semibold text-[color:var(--yn-ink)] hover:no-underline text-left py-5"
                      >
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[16px] leading-relaxed text-[color:var(--yn-muted)] pb-5">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          )}
        </div>
      </Section>

      <PageBadgeBand heading="Ready when you are." />

      <Section>
        <div className="max-w-3xl">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            Still stuck?
          </p>
          <h2 className="mt-3 font-heading text-3xl font-bold text-[color:var(--yn-ink)] tracking-tight">
            Ask a human.
          </h2>
          <p className="mt-4 text-[16px] leading-relaxed text-[color:var(--yn-muted)]">
            Email{" "}
            <a
              href="mailto:info@concepts-simplified.com"
              className="text-[color:var(--yn-primary)] hover:underline"
            >
              info@concepts-simplified.com
            </a>{" "}
            or call{" "}
            <a
              href="tel:+18324231808"
              className="text-[color:var(--yn-primary)] hover:underline"
            >
              +1 (832) 423-1808
            </a>
            . For privacy-specific questions, write{" "}
            <a
              href="mailto:privacy@concepts-simplified.com"
              className="text-[color:var(--yn-primary)] hover:underline"
            >
              privacy@concepts-simplified.com
            </a>
            . Or read{" "}
            <Link
              to="/security"
              className="text-[color:var(--yn-primary)] hover:underline"
            >
              Security &amp; Trust
            </Link>{" "}
            for the deeper technical walkthrough.
          </p>
        </div>
      </Section>
    </div>
  );
}
